/**
 * SidebarNavigation component provides a collapsible navigation menu with role-based access control.
 *
 * Key features:
 * - Context-aware navigation items based on user's current location (home vs other pages)
 * - Role-based menu item visibility using user permissions and abilities
 * - Integration with react-burger-menu for mobile-friendly sliding navigation
 * - External link handling for Darwin admin functions
 * - Mouseflow tracking for specific navigation actions
 *
 * The menu adapts its content based on:
 * - User's current location (home/non-home views)
 * - User permissions (create, edit, group management)
 * - Module access (bookings)
 * - Custom URL configurations (SIAN integration)
 */

import React, { FC, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory, useLocation } from "react-router-dom";
import { Text, Divider, Heading } from "@foris/avocado-suite";
import { slide as Menu } from "react-burger-menu";
import useContextUrl from "@common/hooks/useContextUrl";
import * as mouseflow from "@utils/mouseflow";
import { ContextEDH } from "@context/ContextEDH";
import useModulesByCode from "@common/hooks/useModulesByCode";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import { getUrlWithoutTrailingSlash, usePublicConfigStore } from "@context/usePublicConfigStore";
import styles from "./sidebarNavigation.module.scss";
import { useSettingsStore } from "@context/useSettingsStore";

/**
 * Interface representing the visibility state of the burger menu
 */
export interface MenuVisibilityState {
  isOpen: boolean;
}

/**
 * Props for the SidebarNavigation component
 * @property {boolean} isVisible - Controls the visibility state of the sidebar
 * @property {function} onSidebarToggle - Callback function to handle sidebar visibility changes
 */
interface SidebarNavigationProps {
  isVisible: boolean;
  onSidebarToggle: (state: boolean) => void;
}

const GROUPS_MANAGER = "groups-manager";
const BOOKING = "booking";
const HOME = "home";
const SCHEDULER_EDITOR = "scheduler-editor";

const NAVIGATION_TITLES = {
  [GROUPS_MANAGER]: "sidebar-menu.titles.groups-manager",
  [BOOKING]: "sidebar-menu.titles.booking",
  [HOME]: "sidebar-menu.titles.home",
  [SCHEDULER_EDITOR]: "sidebar-menu.titles.scheduler-editor",
};

const SidebarNavigation: FC<SidebarNavigationProps> = ({ isVisible, onSidebarToggle }) => {
  const history = useHistory();
  const location = useLocation();
  const { modulesByCode } = useModulesByCode();
  const { state } = useContext(ContextEDH);
  const base = state?.base?.base;

  const darwinLegacyUrl = usePublicConfigStore(state => state.darwinLegacyUrl);

  const userCanCreate = base?.user?.permissions?.create;
  const userCanAccessToGroupsManager = base?.user?.abilities?.can_access_group_manager;
  const userCanDeleteSessionsInHolidays = base?.user?.abilities?.can_delete_sessions_in_holidays;
  const isMicroprogrammingStructuresEnabled = useSettingsStore(
    state => state.isProgrammingStructuresEnabled,
  );

  const sianLink = useMemo(() => {
    return (base?.customUrls ?? []).find(url => url.code === "SIAN");
  }, [base?.customUrls]);

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const {
    adaptedDarwinUrl,
    advancedSearchUrl,
    groupsManagerUrl,
    createSectionUrl,
    createPackageUrl,
    packagesAndGroupsReportUrl,
    microprogrammingGroupsReportUrl,
    getPackageClashReportUrl,
    changeHistoryUrl,
    linkChangeHistoryUrl,
    holidaysUrl,
  } = useNavigationUrl();

  const { origin, scenario } = useContextUrl();

  const showMicroprogrammingStructuresInReports =
    !!adaptedDarwinUrl && isMicroprogrammingStructuresEnabled;

  const userManagerUrl = useMemo(() => {
    const urlParams = `?scenario_id=${scenario}${origin ? `&origin_id=${origin}` : ""}`;

    if (modulesByCode?.["user_manager"]?.url) {
      return `${getUrlWithoutTrailingSlash(modulesByCode?.["user_manager"]?.url)}${urlParams}`;
    }

    return `${getUrlWithoutTrailingSlash(darwinLegacyUrl)}/admin${urlParams}`;
  }, [modulesByCode, darwinLegacyUrl, scenario, origin]);

  // Determines if user can edit link sections based on abilities and group creation requirements
  const userCanEditLinkSection = useMemo(() => {
    const abilities = base?.user?.abilities;
    const createGroupWithinLinkCreation = state?.base?.createGroupWithinLinkCreation;

    if (createGroupWithinLinkCreation === "MANDATORY") {
      return abilities?.can_create_groups_in_bundle_view && abilities?.can_edit_links_and_sections;
    }

    return abilities?.can_edit_links_and_sections;
  }, [base?.user, state?.base?.createGroupWithinLinkCreation]);

  // Construct report URLs only if Darwin login is configured
  const groupsAndPackageReportUrl = !!adaptedDarwinUrl
    ? `${adaptedDarwinUrl}/groups_manager/group_packages/${origin}?scenario_id=${scenario}`
    : "";
  const packageClashReportUrl = !!adaptedDarwinUrl
    ? `${adaptedDarwinUrl}/resultados/report_package_clashes_edh/${origin}?scenario_id=${scenario}`
    : "";

  // Determines if user is on home page to show appropriate navigation items
  const isHome = location?.pathname?.includes("home") || location?.pathname === "/";

  const sidebarTitle = useMemo(() => {
    if (location?.pathname?.includes("groups-manager")) {
      return {
        title: t(NAVIGATION_TITLES[GROUPS_MANAGER]),
        key: GROUPS_MANAGER,
      };
    }

    if (location?.pathname?.includes("booking")) {
      return {
        title: t(NAVIGATION_TITLES[BOOKING]),
        key: BOOKING,
      };
    }

    if (isHome) {
      return {
        title: t(NAVIGATION_TITLES[HOME]),
        key: HOME,
      };
    }

    return {
      title: t(NAVIGATION_TITLES[SCHEDULER_EDITOR]),
      key: SCHEDULER_EDITOR,
    };
  }, [location?.pathname, isHome, language]);

  return (
    <Menu
      isOpen={isVisible}
      className={styles.sidebarNavigation}
      onStateChange={(state: MenuVisibilityState) => onSidebarToggle(state.isOpen)}
    >
      <div className={styles.sidebarNavigation_title}>
        <Heading type="h3">{sidebarTitle?.title}</Heading>
      </div>

      {/* Home links */}
      {isHome && (
        <div className={styles.sidebarNavigation_content}>
          <Text className={styles.sidebarTitle}>{t("sidebar-menu.administration.title")}</Text>

          <a
            href={`${userManagerUrl}&account-panel=true`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.sidebarItem}
          >
            {t("sidebar-menu.administration.modify-account")}
          </a>

          <a
            href={`${userManagerUrl}`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.sidebarItem}
          >
            {t("sidebar-menu.administration.configuration")}
          </a>

          <a
            href={`${adaptedDarwinUrl}/ambients?scenario_id=${scenario}${
              origin ? `&origin_id=${origin}` : ""
            }`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.sidebarItem}
          >
            {t("sidebar-menu.administration.manage-environments")}
          </a>

          <a
            href={`${adaptedDarwinUrl}/scenarios?scenario_id=${scenario}${
              origin ? `&origin_id=${origin}` : ""
            }`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.sidebarItem}
          >
            {t("sidebar-menu.administration.manage-scenarios")}
          </a>
        </div>
      )}

      {/* Groups manager links */}
      {sidebarTitle?.key === GROUPS_MANAGER && (
        <div className={styles.sidebarNavigation_content}>
          {userCanAccessToGroupsManager && (
            <Link to={`${groupsManagerUrl()}`} className={styles.sidebarItem}>
              {t("sidebar-menu.administration.groups-management")}
            </Link>
          )}

          <Divider className={styles.sidebarDivider} />

          <Text className={styles.sidebarTitle}>{t("sidebar-menu.reports.title")}</Text>

          {!!groupsAndPackageReportUrl && (
            <Link to={`${packagesAndGroupsReportUrl()}`} className={styles.sidebarItem}>
              {t("sidebar-menu.reports.package-and-group")}
            </Link>
          )}

          {packageClashReportUrl && (
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={`${getPackageClashReportUrl()}`}
              className={styles.sidebarItem}
            >
              {t("sidebar-menu.reports.package")}
            </a>
          )}

          {showMicroprogrammingStructuresInReports && (
            <Link to={`${microprogrammingGroupsReportUrl()}`} className={styles.sidebarItem}>
              {t("sidebar-menu.reports.microprogramming-groups")}
            </Link>
          )}

          <Divider className={styles.sidebarDivider} />

          <Text className={styles.sidebarTitle}>{t("sidebar-menu.histories.title")}</Text>

          {userCanAccessToGroupsManager && (
            <Link
              to={`${changeHistoryUrl()}`}
              className={styles.sidebarItem}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                mouseflow.actionTag(
                  "action_groups_manager_main_change_history",
                  state?.base?.isMouseflowEnabled,
                );
                history.push(changeHistoryUrl());
              }}
            >
              {t("sidebar-menu.histories.group-change-history")}
            </Link>
          )}

          {userCanAccessToGroupsManager && sianLink?.url && (
            <>
              <Divider className={styles.sidebarDivider} />
              <Text className={styles.sidebarTitle}>
                {t("sidebar-menu.special-accesses.title")}
              </Text>
              <a
                href={sianLink?.url}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.sidebarItem}
              >
                {t("sidebar-menu.special-accesses.request-vacancies")}
              </a>
            </>
          )}
        </div>
      )}

      {/* Scheduler editor links */}
      {sidebarTitle?.key === SCHEDULER_EDITOR && (
        <div className={styles.sidebarNavigation_content}>
          <Link to={`${advancedSearchUrl()}`} className={styles.sidebarItem}>
            {t("sidebar-menu.administration.search-advances")}
          </Link>

          <Divider className={styles.sidebarDivider} />

          <Text className={styles.sidebarTitle}>{t("sidebar-menu.creation.title")}</Text>

          {userCanCreate && userCanEditLinkSection && (
            <Link to={`${createSectionUrl()}`} className={styles.sidebarItem}>
              {t("sidebar-menu.creation.create-link")}
            </Link>
          )}

          {userCanCreate && !!base?.user?.abilities?.can_edit_packages && (
            <Link to={`${createPackageUrl()}`} className={styles.sidebarItem}>
              {t("sidebar-menu.creation.create-package")}
            </Link>
          )}

          <Divider className={styles.sidebarDivider} />

          <Text className={styles.sidebarTitle}>{t("sidebar-menu.reports.title")}</Text>

          {!!groupsAndPackageReportUrl && (
            <Link to={`${packagesAndGroupsReportUrl()}`} className={styles.sidebarItem}>
              {t("sidebar-menu.reports.package-and-group")}
            </Link>
          )}

          {packageClashReportUrl && (
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={`${getPackageClashReportUrl()}`}
              className={styles.sidebarItem}
            >
              {t("sidebar-menu.reports.package")}
            </a>
          )}

          {showMicroprogrammingStructuresInReports && (
            <Link to={`${microprogrammingGroupsReportUrl()}`} className={styles.sidebarItem}>
              {t("sidebar-menu.reports.microprogramming-groups")}
            </Link>
          )}

          <Divider className={styles.sidebarDivider} />

          <Text className={styles.sidebarTitle}>{t("sidebar-menu.histories.title")}</Text>

          <Link to={`${linkChangeHistoryUrl()}`} className={styles.sidebarItem}>
            {t("sidebar-menu.histories.change-history")}
          </Link>

          <Divider className={styles.sidebarDivider} />

          {!!userCanDeleteSessionsInHolidays && (
            <>
              <Text className={styles.sidebarTitle}>{t("sidebar-menu.mass-operations.title")}</Text>
              <Link to={`${holidaysUrl()}`} className={styles.sidebarItem}>
                {t("sidebar-menu.mass-operations.delete-sessions-on-holidays")}
              </Link>
            </>
          )}
        </div>
      )}
    </Menu>
  );
};

export default SidebarNavigation;
